import ExpiryMap from 'expiry-map'
import pMemoize from 'p-memoize'

import { AI_CHATBOT_CONFIG } from '../config'
import type { AiChatBotTool } from '../types'

// Module-level registry so clearToolCache() can evict all caches.
const registry: Array<{ clear: () => void }> = []

export function withCache<T>(
  tool: AiChatBotTool<T>,
  ttlMs: number = AI_CHATBOT_CONFIG.toolCache.singleTtlMs,
): AiChatBotTool<T> {
  // Non-cacheable tools must never be cached — either because they have side
  // effects (navigate_to) or because their result changes between calls (get_current_page).
  if (!tool.cacheable) return tool

  // ExpiryMap<string, unknown> satisfies p-memoize's CacheStorage<string, unknown>
  const cache = new ExpiryMap<string, unknown>(ttlMs)
  registry.push(cache)

  const memoized = pMemoize((args: T) => tool.run(args), {
    cache,
    cacheKey: (inputArgs: readonly [T]) => stableStringify(inputArgs[0]),
  })

  return { ...tool, run: memoized }
}

export function clearToolCache(): void {
  for (const c of registry) c.clear()
}

function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return JSON.stringify(value)
  }
  const sorted = Object.keys(value as object)
    .sort()
    .reduce<Record<string, unknown>>((acc, k) => {
      acc[k] = (value as Record<string, unknown>)[k]
      return acc
    }, {})
  return JSON.stringify(sorted)
}
